import React from 'react';
import { WebUiButton } from 'src/modules/web-ui/components/button';
import { WebUiInput } from 'src/modules/web-ui/components/input';
import { WebUiSelect } from 'src/modules/web-ui/components/select';

type VlanMode = 'untagged' | 'tagged' | '-';
type Bridge = { vlan_id: number; name?: string };
type PortCfg = {
  default_vlan_id: number;
  discard_tagged: boolean;
  discard_untagged: boolean;
  force_default_vlan_id: boolean;
  vlan_map: Record<string, Exclude<VlanMode, '-'>>;
};

export default function VlanPortSettingsTable({
  ports,
  bridges,
  portSettings,
  readonly,
  onPortPatch,
  onMembershipChange,
  onRemoveVid,
}: {
  ports: string[];
  bridges: Bridge[];
  portSettings: Record<string, PortCfg>;
  readonly: boolean;
  onPortPatch: (port: string, patch: Partial<PortCfg>) => void;
  onMembershipChange: (port: string, vid: number, mode: VlanMode) => void;
  onRemoveVid: (vid: number) => void;
}) {
  return (
    <div className="mt-6 flex w-full flex-col gap-2">
      <table className="webui-table w-full">
        <thead>
          <tr className="webui-table-row-head">
            <th colSpan={ports.length + 3}>Ingress</th>
          </tr>
          <tr className="webui-table-row-head">
            <th colSpan={2}>Setting</th>
            {ports.map((p) => (
              <th key={`h-in-${p}`}>{p}</th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          <tr className="webui-table-row-content">
            <td colSpan={2}>Default VLAN ID</td>
            {ports.map((p) => (
              <td key={`def-${p}`}>
                <WebUiInput
                  inputSize="sm"
                  type="number"
                  min={1}
                  max={4094}
                  disabled={readonly}
                  value={portSettings[p]?.default_vlan_id ?? 1}
                  onChange={(e) =>
                    onPortPatch(p, { default_vlan_id: Number(e.target.value) })
                  }
                />
              </td>
            ))}
            <td />
          </tr>
          <tr className="webui-table-row-content">
            <td colSpan={2}>Discard Tagged</td>
            {ports.map((p) => (
              <td key={`dt-${p}`}>
                <WebUiInput
                  inputSize="sm"
                  type="checkbox"
                  disabled={readonly || p.startsWith('Bond')}
                  checked={
                    p.startsWith('Bond')
                      ? false
                      : Boolean(portSettings[p]?.discard_tagged)
                  }
                  onChange={(e) =>
                    onPortPatch(p, { discard_tagged: e.target.checked })
                  }
                />
              </td>
            ))}
            <td />
          </tr>
          <tr className="webui-table-row-content">
            <td colSpan={2}>Discard Untagged</td>
            {ports.map((p) => (
              <td key={`du-${p}`}>
                <WebUiInput
                  inputSize="sm"
                  type="checkbox"
                  disabled={readonly || p.startsWith('Bond')}
                  checked={
                    p.startsWith('Bond')
                      ? false
                      : Boolean(portSettings[p]?.discard_untagged)
                  }
                  onChange={(e) =>
                    onPortPatch(p, { discard_untagged: e.target.checked })
                  }
                />
              </td>
            ))}
            <td />
          </tr>
          <tr className="webui-table-row-content">
            <td colSpan={2}>Force Default VLAN ID</td>
            {ports.map((p) => (
              <td key={`fd-${p}`}>
                <WebUiInput
                  inputSize="sm"
                  type="checkbox"
                  disabled={readonly || p.startsWith('Bond')}
                  checked={
                    p.startsWith('Bond')
                      ? false
                      : Boolean(portSettings[p]?.force_default_vlan_id)
                  }
                  onChange={(e) =>
                    onPortPatch(p, { force_default_vlan_id: e.target.checked })
                  }
                />
              </td>
            ))}
            <td />
          </tr>
        </tbody>
        <thead>
          <tr className="webui-table-row-head">
            <th colSpan={ports.length + 3}>VLAN Members and Egress Settings</th>
          </tr>
          <tr className="webui-table-row-head">
            <th>Setting</th>
            <th>Name</th>
            {ports.map((p) => (
              <th key={`h-eg-${p}`}>{p}</th>
            ))}
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {bridges.map((b) => (
            <tr key={b.vlan_id} className="webui-table-row-content">
              <td className="text-center">{b.vlan_id}</td>
              <td className="text-center">{b.name ?? ''}</td>
              {ports.map((p) => {
                const membership = (portSettings[p]?.vlan_map?.[
                  String(b.vlan_id)
                ] ?? '-') as VlanMode;
                return (
                  <td key={`${p}-${b.vlan_id}`}>
                    <WebUiSelect
                      inputSize="sm"
                      options={[
                        { label: '-', value: '-' },
                        { label: 'untagged', value: 'untagged' },
                        { label: 'tagged', value: 'tagged' },
                      ]}
                      disabled={readonly}
                      value={membership}
                      onChange={(e) =>
                        onMembershipChange(
                          p,
                          b.vlan_id,
                          e.target.value as VlanMode,
                        )
                      }
                    />
                  </td>
                );
              })}
              <td className="text-center">
                <WebUiButton
                  disabled={readonly}
                  onClick={() => onRemoveVid(b.vlan_id)}
                >
                  remove vlan id {b.vlan_id}
                </WebUiButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
